import type { ModelDefinition, ProviderDefinition } from "@routerbench/models";

// Model prices are quoted in USD — the industry-standard unit for LLM APIs
// (OpenAI/Anthropic/Google all publish $/1M tokens). The public catalog is
// region-agnostic, so it shows USD directly.
export function formatPrice(price: number | undefined): string {
	if (!price) {
		return "Free";
	}
	if (price < 0.000001) {
		return `$${(price * 1000000).toFixed(2)}/1M tokens`;
	}
	if (price < 0.001) {
		return `$${(price * 1000).toFixed(2)}/1K tokens`;
	}
	return `$${price.toFixed(4)}/token`;
}

export function formatContextSize(size: number | undefined): string {
	if (!size) {
		return "Unknown";
	}
	if (size >= 1000000) {
		return `${(size / 1000000).toFixed(1)}M tokens`;
	}
	if (size >= 1000) {
		return `${(size / 1000).toFixed(0)}K tokens`;
	}
	return `${size} tokens`;
}

export function getProviderForModel(
	model: ModelDefinition,
	providers: ProviderDefinition[],
): ProviderDefinition | undefined {
	const primaryProvider = model.providers[0];
	return providers.find((p) => p.id === primaryProvider?.providerId);
}

export type ModelType =
	| "chat"
	| "embeddings"
	| "image"
	| "video"
	| "audio_speech"
	| "audio_transcription"
	| "audio_music";

/**
 * Determine model type from model definition or model name.
 * Used for badges, filters, and table columns.
 */
export function getModelType(model: ModelDefinition): ModelType {
	if (model.output?.includes("video")) {
		return "video";
	}
	if (model.output?.includes("audio_music")) {
		return "audio_music";
	}
	if (model.output?.includes("audio_speech")) {
		return "audio_speech";
	}
	if (model.output?.includes("audio_transcription")) {
		return "audio_transcription";
	}
	if (model.output?.includes("embeddings")) {
		return "embeddings";
	}
	if (model.output?.includes("image") && !model.output?.includes("text")) {
		return "image";
	}
	return "chat";
}

/**
 * Determine model type from a model name string by looking up in models registry.
 */
export function getModelTypeByName(
	modelName: string,
	allModels: ModelDefinition[],
): ModelType {
	const modelDef = allModels.find(
		(m) =>
			m.id === modelName || m.providers.some((p) => p.modelName === modelName),
	);
	if (!modelDef) {
		return "chat";
	}
	return getModelType(modelDef);
}

export function getModelCapabilities(model: ModelDefinition): string[] {
	const capabilities: string[] = [];
	const provider = model.providers[0];

	if (provider?.streaming) {
		capabilities.push("Streaming");
	}
	if (provider?.vision) {
		capabilities.push("Vision");
	}
	if (provider?.tools) {
		capabilities.push("Tools");
	}
	if (provider?.reasoning) {
		capabilities.push("Reasoning");
	}
	if (provider?.jsonOutput) {
		capabilities.push("JSON Output");
	}

	if (model.output?.includes("image")) {
		capabilities.push("Image Generation");
	}

	if (model.free) {
		capabilities.push("Free");
	}

	return capabilities;
}
